import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Eye, GraduationCap, RotateCcw, X } from 'lucide-react';
import { EmptyState, PageHeader } from '@/components/ui';
import { VideoPlayer } from '@/components/VideoPlayer';
import {
  answer,
  finishSession,
  loadStudyDeck,
  startSession,
  type SessionResult,
  type StudyFilters,
} from '@/services/study/StudyService';
import { useCategories } from '@/hooks/useCatalogQueries';
import { useMedia } from '@/features/media/MediaProvider';
import type { Sign } from '@/types/catalog';

type Phase = 'setup' | 'studying' | 'summary';

const DECK_SIZE = 20;

export function StudyPage(): React.ReactElement {
  const categories = useCategories();
  const { resolver } = useMedia();

  const [phase, setPhase] = useState<Phase>('setup');
  const [filters, setFilters] = useState<StudyFilters>({});
  const [deck, setDeck] = useState<Sign[]>([]);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [starting, setStarting] = useState(false);
  const [noMatches, setNoMatches] = useState(false);

  const begin = useCallback(async () => {
    setStarting(true);
    try {
      const next = await loadStudyDeck({ ...filters, limit: DECK_SIZE });
      setNoMatches(next.length === 0);
      if (next.length === 0) {
        setDeck([]);
        return;
      }

      setDeck(next);
      setIndex(0);
      setRevealed(false);
      setSessionId(await startSession(filters, next.length));
      setPhase('studying');
    } finally {
      setStarting(false);
    }
  }, [filters]);

  const respond = useCallback(
    async (correct: boolean) => {
      const current = deck[index];
      if (!current || !sessionId) return;

      await answer(sessionId, current.id, correct);

      if (index + 1 >= deck.length) {
        setResult(await finishSession(sessionId));
        setPhase('summary');
        return;
      }

      setIndex((value) => value + 1);
      setRevealed(false);
    },
    [deck, index, sessionId],
  );

  // Reinicia ao sair da tela, para não retomar uma sessão de dias atrás.
  useEffect(() => () => setPhase('setup'), []);

  if (phase === 'setup') {
    return (
      <>
        <PageHeader title="Estudar" />

        <p className="text-sm text-slate-400">
          Assista ao sinal, tente lembrar o nome e confira. Os sinais que você erra
          voltam antes.
        </p>

        <fieldset className="mt-5">
          <legend className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Categorias
          </legend>

          <div className="mt-2 flex flex-wrap gap-2">
            <FilterChip
              label="Todas"
              active={!filters.categoryIds?.length}
              onClick={() => setFilters((current) => ({ ...current, categoryIds: [] }))}
            />
            {(categories ?? []).map((category) => {
              const active = filters.categoryIds?.includes(category.id) ?? false;
              return (
                <FilterChip
                  key={category.id}
                  label={category.name}
                  active={active}
                  onClick={() =>
                    setFilters((current) => {
                      const selected = new Set(current.categoryIds ?? []);
                      if (active) selected.delete(category.id);
                      else selected.add(category.id);
                      return { ...current, categoryIds: [...selected] };
                    })
                  }
                />
              );
            })}
          </div>
        </fieldset>

        <fieldset className="mt-5">
          <legend className="text-sm font-semibold uppercase tracking-wide text-slate-400">
            Filtros
          </legend>

          <div className="mt-2 flex flex-wrap gap-2">
            <FilterChip
              label="Só favoritos"
              active={filters.onlyFavorites ?? false}
              onClick={() =>
                setFilters((current) => ({ ...current, onlyFavorites: !current.onlyFavorites }))
              }
            />
            <FilterChip
              label="Só disponíveis offline"
              active={filters.onlyOffline ?? false}
              onClick={() =>
                setFilters((current) => ({ ...current, onlyOffline: !current.onlyOffline }))
              }
            />
          </div>
        </fieldset>

        {/* Só depois de tentar montar o baralho — antes disso a lista vazia é
            apenas o estado inicial, não uma ausência de resultados. */}
        {noMatches && !starting && (
          <p role="status" className="mt-4 rounded-xl bg-amber-500/15 p-3 text-sm text-amber-200">
            Nenhum sinal corresponde a esses filtros.
          </p>
        )}

        <button
          type="button"
          onClick={() => void begin()}
          disabled={starting}
          className="mt-6 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-sky-500 font-medium text-slate-950 active:bg-sky-600 disabled:bg-slate-700 disabled:text-slate-400"
        >
          <GraduationCap size={18} aria-hidden />
          Começar
        </button>
      </>
    );
  }

  if (phase === 'summary' && result) {
    return (
      <>
        <PageHeader title="Resumo da sessão" />

        <div className="rounded-xl bg-slate-800 p-5 text-center">
          <p className="text-3xl font-semibold tabular-nums">
            {result.correct} <span className="text-slate-500">/</span> {result.total}
          </p>
          <p className="mt-1 text-sm text-slate-400">
            {Math.round(result.accuracy * 100)}% de acerto
          </p>

          <dl className="mt-4 flex justify-center gap-6 text-sm">
            <div>
              <dt className="text-slate-400">Acertos</dt>
              <dd className="text-emerald-300 tabular-nums">{result.correct}</dd>
            </div>
            <div>
              <dt className="text-slate-400">Erros</dt>
              <dd className="text-amber-300 tabular-nums">{result.incorrect}</dd>
            </div>
          </dl>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => void begin()}
            className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-sky-500 text-sm font-medium text-slate-950 active:bg-sky-600"
          >
            <RotateCcw size={18} aria-hidden />
            Estudar de novo
          </button>
          <button
            type="button"
            onClick={() => setPhase('setup')}
            className="min-h-12 flex-1 rounded-xl bg-slate-800 text-sm font-medium active:bg-slate-700"
          >
            Mudar filtros
          </button>
        </div>
      </>
    );
  }

  const current = deck[index];
  if (!current) {
    return (
      <>
        <PageHeader title="Estudar" />
        <EmptyState title="Nada para estudar" description="Ajuste os filtros e tente de novo." />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Estudar"
        subtitle={`${index + 1} de ${deck.length}`}
        actions={
          <button
            type="button"
            onClick={() => setPhase('setup')}
            className="min-h-11 rounded-lg px-3 text-sm text-slate-400 active:bg-slate-800"
          >
            Sair
          </button>
        }
      />

      <div
        className="mb-3 h-1 overflow-hidden rounded-full bg-slate-800"
        role="progressbar"
        aria-valuenow={index + 1}
        aria-valuemin={1}
        aria-valuemax={deck.length}
        aria-label={`Sinal ${index + 1} de ${deck.length}`}
      >
        <div
          className="h-full bg-sky-400 transition-[width]"
          style={{ width: `${((index + 1) / deck.length) * 100}%` }}
        />
      </div>

      <VideoPlayer key={current.id} sign={current} resolver={resolver} />

      {/* O nome é a resposta: fica escondido até o usuário pedir (F16-T07). */}
      <div className="mt-4 min-h-20 rounded-xl bg-slate-800 p-4 text-center">
        {revealed ? (
          <>
            <p className="text-lg font-semibold">{current.name}</p>
            {current.reference && (
              <p className="mt-1 text-sm text-slate-400">{current.reference}</p>
            )}
            <Link
              to={`/sign/${encodeURIComponent(current.id)}`}
              className="mt-2 inline-block text-sm text-sky-400"
            >
              Ver ficha do sinal
            </Link>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setRevealed(true)}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-slate-700 font-medium active:bg-slate-600"
          >
            <Eye size={18} aria-hidden />
            Revelar nome
          </button>
        )}
      </div>

      {revealed && (
        <div className="mt-3 flex gap-2 pb-4">
          <button
            type="button"
            onClick={() => void respond(false)}
            className="flex min-h-14 flex-1 items-center justify-center gap-2 rounded-xl bg-amber-500/15 font-medium text-amber-200 active:bg-amber-500/25"
          >
            <X size={20} aria-hidden />
            Não lembrei
          </button>
          <button
            type="button"
            onClick={() => void respond(true)}
            className="flex min-h-14 flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-500/15 font-medium text-emerald-200 active:bg-emerald-500/25"
          >
            <Check size={20} aria-hidden />
            Acertei
          </button>
        </div>
      )}
    </>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-11 rounded-full px-4 text-sm ${
        active ? 'bg-sky-500/20 text-sky-300' : 'bg-slate-800 text-slate-300 active:bg-slate-700'
      }`}
    >
      {label}
    </button>
  );
}
